import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'

// The stepper remounts when the page swaps from the eligibility check to the
// claim form, so remember the last step shown and animate on from it.
let lastStep: 1 | 2 | 3 = 1

interface StepperProps {
    currentStep: 1 | 2 | 3
}

const PhaseTick = () => (
    <svg className={styles.tick} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="8" fill="currentColor" />
        <path
            d="M4.5 8.2l2.3 2.3 4.7-4.9"
            fill="none"
            stroke="var(--phase-tick)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

// A slim three-phase label row that sits directly on the journey progress bar,
// so the phase name and the bar read as one piece instead of two trackers.
const Stepper: React.FC<StepperProps> = ({ currentStep }) => {
    const [shownStep, setShownStep] = useState<1 | 2 | 3>(lastStep)

    useEffect(() => {
        const frame = requestAnimationFrame(() => setShownStep(currentStep))
        lastStep = currentStep
        return () => cancelAnimationFrame(frame)
    }, [currentStep])

    return (
        // data-scroll-anchor: where the claim form scrolls back to between steps
        <ol className={styles.phases} aria-label="Claim stages" data-scroll-anchor="">
            <li
                className={`${styles.phase} ${shownStep > 1 ? styles.done : styles.current}`}
                aria-current={shownStep === 1 ? 'step' : undefined}
            >
                {shownStep > 1 ? (
                    <PhaseTick />
                ) : (
                    <span className={styles.number} aria-hidden="true">
                        1
                    </span>
                )}
                Eligibility
                {shownStep > 1 && <span className={styles.visuallyHidden}> (complete)</span>}
            </li>
            <li
                className={`${styles.phase} ${
                    shownStep > 2 ? styles.done : shownStep === 2 ? styles.current : ''
                }`}
                aria-current={shownStep === 2 ? 'step' : undefined}
            >
                {shownStep > 2 ? (
                    <PhaseTick />
                ) : (
                    <span className={styles.number} aria-hidden="true">
                        2
                    </span>
                )}
                Your details
                {shownStep > 2 && <span className={styles.visuallyHidden}> (complete)</span>}
            </li>
            <li
                className={`${styles.phase} ${shownStep === 3 ? styles.current : ''}`}
                aria-current={shownStep === 3 ? 'step' : undefined}
            >
                <span className={styles.number} aria-hidden="true">
                    3
                </span>
                Sign
            </li>
        </ol>
    )
}

export default Stepper
